import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/news";
import { formatDate, formatViews, getCategoryName } from "@/utils/news";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStart = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setIndex(((next % items.length) + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="w-full h-[360px] md:h-[520px] rounded-lg bg-background-200 flex flex-col items-center justify-center text-foreground-500">
        <i className="ri-image-line text-4xl mb-3" />
        <p>暂无焦点新闻，敬请期待</p>
      </div>
    );
  }

  const current = items[index];

  return (
    <section
      className="relative w-full h-[360px] md:h-[520px] rounded-lg overflow-hidden bg-foreground-900"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) go(diff > 0 ? index - 1 : index + 1);
        touchStart.current = null;
      }}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 吃瓜爆料`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10" />

      <Link to={`/detail/${current.id}`} className="absolute inset-0 flex flex-col justify-end p-5 md:p-10 w-full">
        <div className="w-full max-w-3xl">
          <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-3">
            <i className="ri-fire-line" /> 焦点爆料
          </span>
          <h2 className="text-xl md:text-3xl font-black text-background-50 leading-snug drop-shadow line-clamp-2">
            {current.title}
          </h2>
          <p className="hidden sm:block text-sm md:text-base text-background-100/90 mt-3 leading-relaxed line-clamp-2">
            {current.summary}
          </p>
          <div className="flex items-center gap-4 flex-wrap mt-4 text-xs md:text-sm text-background-100/80">
            <span className="inline-flex items-center gap-1">
              <i className="ri-bookmark-line" />
              {getCategoryName(current.categoryId)}
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <i className="ri-time-line" />
              {formatDate(current.publishTime)}
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" />
              {formatViews(current.views)}
            </span>
          </div>
        </div>
      </Link>

      <div className="absolute bottom-5 right-5 flex items-center gap-2">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur flex items-center justify-center text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur flex items-center justify-center text-background-50 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-6 left-5 md:left-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}